import React, { useState, useEffect, useMemo, useContext, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { useGeo } from "../../../context/GeoContext";
import { useCart } from "../../../context/CartContext";
import { validateCheckoutInput } from "../../../utils/CheckoutErrorHandler";
import {
  initiateGuestOrder,
  createWalletOrder,
  initiateUserStripeOrder,
  initiateGuestMultipleOrder,
  createWalletMultipleOrder,
  initiateUserStripeMultipleOrder,
} from "../../../services/OrderService";
import { validateVoucher as validateVoucherApi } from "../../../services/VoucherService";
import { purchaseTopUp } from "../../../services/TopUpService";

import { fetchPackageById } from "../../../services/PackagesService";

import AuthPanel from "../AuthPanel/AuthPanel";
import OrderSummary from "../OrderSummary/OrderSummary";
import PaymentModal from "../PaymentModal/PaymentModal";
import StatusModal from "../StatusModal/StatusModal";
import { trackAnalyticsEvent } from "../../../analytics/analytics";
import { appendCampaignParamsToUrl } from "../../../analytics/campaignTracker";
import "../CheckoutPanel/CheckoutPanel.css";
import { useTranslation } from "react-i18next";

const PENDING_CHECKOUT_PROVIDER_KEY = "pending_checkout_payment_provider";

const rememberCheckoutPaymentProvider = (provider) => {
  try {
    localStorage.setItem(PENDING_CHECKOUT_PROVIDER_KEY, provider);
  } catch (storageError) {
    console.error("Failed to save checkout payment provider:", storageError);
  }
};

const CheckoutLayout = ({ status }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || "en";

  const { items, clear } = useCart();
  const { token, wallet, walletCurrency, loading: authLoading, newsletter, userId } = useContext(AuthContext);

  const geoContext = useGeo() || {};
  const { geoCountryCode, geoReady, ensureGeoLoaded } = geoContext;

  const activeCurrency = walletCurrency || "EUR";
  const isLoggedIn = !!token;

  const [newsletterOptInGuest, setNewsletterOptInGuest] = useState(false);
  const showNewsletterCheckbox = !isLoggedIn || !newsletter;
  const [turnstileToken, setTurnstileToken] = useState("");
  const [shouldShowTurnstile, setShouldShowTurnstile] = useState(false);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [isPayPalPayment, setIsPayPalPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [speedConfirmed, setSpeedConfirmed] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [packages, setPackages] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [promocodeData, setPromocodeData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [finalStatus, setFinalStatus] = useState(status);

  const [frozenItems, setFrozenItems] = useState(() => {
    if (items && items.length > 0) return items;
    try {
      const raw = localStorage.getItem("pending_purchase") || localStorage.getItem("pending_stripe_purchase");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.items && Array.isArray(parsed.items) && parsed.items.length > 0) {
          return parsed.items;
        }
      }
    } catch {}
    return [];
  });

  const purchaseTrackedRef = useRef(false);

  useEffect(() => {
    ensureGeoLoaded?.();
  }, [ensureGeoLoaded]);

  useEffect(() => {
    if (finalStatus !== "success" && items.length > 0) {
      setFrozenItems(items);
    }
  }, [items, finalStatus]);

  const displayItems = finalStatus === "success" && frozenItems.length > 0 ? frozenItems : items;
  const totalUnits = displayItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const hasMultiplePackages = totalUnits > 1 || displayItems.length > 1;

  useEffect(() => {
    if (!status) return;

    if (status === "success" || status === "canceled") {
      let paymentProvider = null;
      try {
        paymentProvider = localStorage.getItem(PENDING_CHECKOUT_PROVIDER_KEY);
        localStorage.removeItem(PENDING_CHECKOUT_PROVIDER_KEY);
      } catch (storageError) {
        console.error("Failed to read checkout payment provider:", storageError);
      }
      setIsPayPalPayment(status === "success" && paymentProvider === "paypal");
    }

    setFinalStatus(status);

    if (status === "success" || status === "canceled") {
      setShowStatusModal(true);
    }
  }, [status]);

  useEffect(() => {
    if (finalStatus === "success" || finalStatus === "canceled") {
      setShowStatusModal(true);
    }
  }, [finalStatus]);

  const savePendingPurchase = (
    itemsToSave,
    promo,
    curr,
    provider = "stripe",
    transactionId = null,
    isTopUp = false,
    extra = {},
  ) => {
    const rawPrice = (itemsToSave || []).reduce((sum, it) => sum + (it.price || 0) * (it.quantity || 1), 0);
    const discountAmount = promo?.amount || 0;
    const finalValue = Math.max(0, rawPrice - discountAmount);
    const paymentType = provider === "paypal" ? "paypal" : "card";
    const purchaseType = isTopUp ? "esim_topup" : "esim_purchase";

    const pendingData = {
      transaction_id: String(transactionId || `order_${Date.now()}`),
      stripe_session_id: extra.stripe_session_id || "",
      payment_id: String(extra.payment_id || ""),
      user_id: String(extra.user_id || email || userId || ""),
      value: finalValue,
      price: rawPrice,
      currency: curr,
      coupon: promo?.code || "",
      payment_type: paymentType,
      purchase_type: purchaseType,
      items: (itemsToSave || []).map((item) => ({
        item_id: String(item.internal_package_id || item.id || ""),
        item_name: item.name || (isTopUp ? "eSIM Top-Up" : "eSIM Package"),
        price: Number(item.price || 0),
        quantity: Number(item.quantity || 1),
        item_category: isTopUp ? "Top-Up" : "eSIM",
        currency: curr,
      })),
      createdAt: Date.now(),
    };

    try {
      localStorage.setItem("pending_purchase", JSON.stringify(pendingData));
      localStorage.setItem("pending_stripe_purchase", JSON.stringify(pendingData));
    } catch (e) {
      console.error("Failed to save pending purchase:", e);
    }
  };

  const isTopUpOrder = useMemo(() => {
    return items.some((item) => item.type === "topup" || item.is_topup);
  }, [items]);

  useEffect(() => {
    if (status === "success" && !purchaseTrackedRef.current) {
      let cachedPurchase = null;
      try {
        const raw = localStorage.getItem("pending_purchase") || localStorage.getItem("pending_stripe_purchase");
        if (raw) {
          cachedPurchase = JSON.parse(raw);
          localStorage.removeItem("pending_purchase");
          localStorage.removeItem("pending_stripe_purchase");
        }
      } catch (e) {
        console.error("Failed to parse pending purchase:", e);
      }

      const urlSessionId = new URLSearchParams(window.location.search).get("session_id");

      if (cachedPurchase) {
        purchaseTrackedRef.current = true;
        trackAnalyticsEvent.purchase({
          ...cachedPurchase,
          transactionId: cachedPurchase.transaction_id || urlSessionId || `order_${Date.now()}`,
        });
      } else if (displayItems && displayItems.length > 0) {
        purchaseTrackedRef.current = true;
        const rawPrice = displayItems.reduce((sum, it) => sum + (it.price || 0) * (it.quantity || 1), 0);
        const discountAmount = promocodeData?.amount || 0;
        const finalValue = Math.max(0, rawPrice - discountAmount);

        trackAnalyticsEvent.purchase({
          transactionId: urlSessionId || `order_${Date.now()}`,
          value: finalValue,
          price: rawPrice,
          currency: activeCurrency,
          coupon: promocodeData?.code || "",
          payment_type: "card",
          purchase_type: isTopUpOrder ? "esim_topup" : "esim_purchase",
          items: displayItems.map((item) => ({
            item_id: String(item.internal_package_id || item.id || ""),
            item_name: item.name || (isTopUpOrder ? "eSIM Top-Up" : "eSIM Package"),
            price: Number(item.price || 0),
            quantity: Number(item.quantity || 1),
            item_category: isTopUpOrder ? "Top-Up" : "eSIM",
            currency: activeCurrency,
          })),
        });
      }
    } else if (status === "canceled") {
      localStorage.removeItem("pending_purchase");
      localStorage.removeItem("pending_stripe_purchase");
    }
  }, [status, displayItems, promocodeData, activeCurrency, isTopUpOrder]);

  const packageIds = useMemo(() => {
    const ids = (displayItems || []).map((it) => {
      const id = it.internal_package_id || it.id;
      return id;
    });
    const stringIds = ids.map((x) => String(x));
    return Array.from(new Set(stringIds));
  }, [displayItems]);

  const currency = (value, cur = walletCurrency) => {
    if (value == null) return "";
    return `${value.toFixed(2)} ${cur}`;
  };

  const validationError = useMemo(() => {
    const mail = (email || "").trim();
    return !isLoggedIn ? validateCheckoutInput(mail) : null;
  }, [email, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) return;

    const mail = (email || "").trim();
    const isEmailValid = !validateCheckoutInput(mail);

    if (isEmailValid && !shouldShowTurnstile) {
      setShouldShowTurnstile(true);
    }
  }, [email, isLoggedIn, shouldShowTurnstile]);

  const handleTurnstileVerify = useCallback((token) => {
    setTurnstileToken(token);
    setErrorMessage("");
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken("");
    setErrorMessage(t("errors.securityVerificationExpired", "Security verification expired. Please try again."));
  }, [t]);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken("");
    setErrorMessage(t("errors.securityVerificationFailed", "Security verification failed. Please try again."));
  }, [t]);

  const handleGuestOnlineOrder = async (provider) => {
    if (!turnstileToken) {
      setErrorMessage(t("errors.pleaseCompleteSecurityVerification", "Please complete the security verification."));
      return;
    }

    try {
      setOrderLoading(true);
      let orderData;
      const targetFrontendUrl = appendCampaignParamsToUrl(`${window.location.origin}/${currentLang}/cart`);

      if (hasMultiplePackages) {
        orderData = await initiateGuestMultipleOrder({
          items: displayItems,
          userId: email,
          frontendUrl: targetFrontendUrl,
          currency: activeCurrency,
          newsletter: newsletterOptInGuest,
          turnstileToken,
          provider,
        });
      } else {
        const cartItem = displayItems[0];
        const internalPackageId = cartItem.internal_package_id || cartItem.id;

        orderData = await initiateGuestOrder({
          internalPackageId: internalPackageId,
          userId: email,
          frontendUrl: targetFrontendUrl,
          currency: activeCurrency,
          newsletter: newsletterOptInGuest,
          turnstileToken,
          provider,
        });
      }

      const redirectUrl = orderData.redirect_url;
      if (redirectUrl) {
        const stripeMatch = redirectUrl.match(/(cs_[a-zA-Z0-9_]+)/);
        const stripeSessionId = stripeMatch ? stripeMatch[1] : null;

        savePendingPurchase(
          displayItems,
          promocodeData,
          activeCurrency,
          provider,
          orderData.transaction_id || orderData.payment_id,
          isTopUpOrder,
          {
            stripe_session_id: stripeSessionId,
            payment_id: orderData.payment_id,
            user_id: email || userId || "",
          },
        );
        rememberCheckoutPaymentProvider(provider);
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error(`❌ ${provider} order failed:`, error);
      setErrorMessage(error.response?.data?.detail || "Payment failed");
    } finally {
      setOrderLoading(false);
    }
  };

  const handleWalletOrder = async () => {
    if (!wallet?.id) {
      setErrorMessage(t("errors.walletNotFound", "Wallet not found"));
      setFinalStatus("canceled");
      return false;
    }

    try {
      setOrderLoading(true);
      if (isTopUpOrder) {
        const cartItem = displayItems[0];

        const topUpResponse = await purchaseTopUp({
          iccid: cartItem.target_iccid,
          walletId: wallet.id,
          packageId: cartItem.internal_package_id,
          packageCode: cartItem.package_code,
        });

        const transactionId = topUpResponse?.transaction_id || `topup_${Date.now()}`;
        const itemPrice = Number(cartItem?.price || 0);

        const purchaseData = {
          transactionId,
          user_id: String(userId || email || ""),
          value: itemPrice,
          price: itemPrice,
          currency: activeCurrency,
          coupon: "",
          payment_type: "wallet",
          purchase_type: "esim_topup",
          item_id: String(cartItem?.internal_package_id || cartItem?.id || ""),
          item_name: cartItem?.name || "eSIM Top-Up",
          quantity: 1,
          items: [
            {
              item_id: String(cartItem?.internal_package_id || cartItem?.id || ""),
              item_name: cartItem?.name || "eSIM Top-Up",
              price: itemPrice,
              quantity: 1,
              item_category: "Top-Up",
              currency: activeCurrency,
            },
          ],
        };

        // Always fire standard ecommerce purchase event so GA4 registers the purchase
        trackAnalyticsEvent.purchase(purchaseData);
        trackAnalyticsEvent.esimTopUp(purchaseData);
      } else {
        let orderResult;
        if (hasMultiplePackages) {
          orderResult = await createWalletMultipleOrder({
            items: displayItems,
            walletId: wallet.id,
            promocode: promocodeData ? promocodeData.code : null,
          });
        } else {
          const cartItem = displayItems[0];
          const internalPackageId = cartItem.internal_package_id || cartItem.id;

          orderResult = await createWalletOrder({
            internalPackageId: internalPackageId,
            walletId: wallet.id,
            promocode: promocodeData ? promocodeData.code : null,
          });
        }

        const transactionId = orderResult?.transaction_id || `order_${Date.now()}`;
        const rawPrice = (displayItems || []).reduce((sum, it) => sum + (it.price || 0) * (it.quantity || 1), 0);
        const discountAmount = promocodeData?.amount || 0;
        const finalValue = Math.max(0, rawPrice - discountAmount);

        const orderItems = (displayItems || []).map((item) => ({
          item_id: String(item.internal_package_id || item.id || ""),
          item_name: item.name || "eSIM Package",
          price: Number(item.price || 0),
          quantity: Number(item.quantity || 1),
          item_category: "eSIM",
          currency: activeCurrency,
        }));

        const purchaseData = {
          transactionId,
          user_id: String(userId || email || ""),
          value: finalValue,
          price: rawPrice,
          currency: activeCurrency,
          coupon: promocodeData ? promocodeData.code : "",
          payment_type: "wallet",
          purchase_type: "esim_order",
          items: orderItems,
        };

        // Always fire standard ecommerce purchase event so GA4 registers the purchase
        trackAnalyticsEvent.purchase(purchaseData);
        trackAnalyticsEvent.esimOrder(purchaseData);
      }
      setFinalStatus("success");
      return true;
    } catch (error) {
      console.error("❌ Wallet payment failed:", error);
      setErrorMessage(error.response?.data?.detail || "Payment failed");
      setFinalStatus("canceled");
      return false;
    } finally {
      setOrderLoading(false);
    }
  };

  const handleUserOnlineOrder = async (provider) => {
    try {
      setOrderLoading(true);
      let orderData;
      const targetFrontendUrl = appendCampaignParamsToUrl(`${window.location.origin}/${currentLang}/cart`);

      if (hasMultiplePackages) {
        orderData = await initiateUserStripeMultipleOrder({
          items: displayItems,
          walletId: wallet?.id,
          frontendUrl: targetFrontendUrl,
          promocode: promocodeData ? promocodeData.code : null,
          provider,
        });
      } else {
        const cartItem = displayItems[0];
        const internalPackageId = cartItem.internal_package_id || cartItem.id;

        orderData = await initiateUserStripeOrder({
          internalPackageId: internalPackageId,
          walletId: wallet?.id,
          frontendUrl: targetFrontendUrl,
          promocode: promocodeData ? promocodeData.code : null,
          provider,
        });
      }

      const redirectUrl = orderData.redirect_url;
      if (redirectUrl) {
        const stripeMatch = redirectUrl.match(/(cs_[a-zA-Z0-9_]+)/);
        const stripeSessionId = stripeMatch ? stripeMatch[1] : null;

        savePendingPurchase(
          displayItems,
          promocodeData,
          activeCurrency,
          provider,
          orderData.transaction_id || orderData.payment_id,
          isTopUpOrder,
          {
            stripe_session_id: stripeSessionId,
            payment_id: orderData.payment_id,
            user_id: email || userId || "",
          },
        );
        rememberCheckoutPaymentProvider(provider);
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error(`❌ User ${provider} order failed:`, error);
      setErrorMessage(error.response?.data?.detail || "Payment failed");
    } finally {
      setOrderLoading(false);
    }
  };

  const handlePlaceOrder = () => {
    if (!isLoggedIn && isTopUpOrder) {
      setErrorMessage(t("errors.topUpRequiresLogin", "Top-Up purchases require login. Please sign in."));
      return;
    }
    if (!isLoggedIn && validationError) {
      setErrorMessage(validationError);
      return;
    }

    ensureGeoLoaded?.();
    setShowPaymentModal(true);
  };

  const handlePaymentConfirm = async (provider) => {
    setShowPaymentModal(false);

    if (provider === "wallet") {
      await handleWalletOrder();
    } else if (provider === "paypal" || provider === "stripe") {
      if (isLoggedIn) {
        await handleUserOnlineOrder(provider);
      } else {
        await handleGuestOnlineOrder(provider);
      }
    } else {
      setErrorMessage(t("errors.unsupportedPaymentMethod", "Unsupported payment method."));
    }
  };

  const handleApplyPromocode = async (code) => {
    const data = await validateVoucherApi(code);

    if (data.available && data.promocode?.type === "sell_discount") {
      const promo = { code: data.promocode.code, type: data.promocode.type, amount: data.promocode.amount };
      setPromocodeData(promo);
    } else {
      setPromocodeData(null);
    }
    return data;
  };

  useEffect(() => {
    let cancelled = false;

    const loadPackages = async () => {
      if (!packageIds || packageIds.length === 0) {
        setPackages([]);
        return;
      }

      try {
        const results = await Promise.all(
          packageIds.map(async (pid) => {
            try {
              const data = await fetchPackageById(pid, walletCurrency, i18n.resolvedLanguage);
              return data;
            } catch {
              const cartItem = displayItems.find((it) => it.internal_package_id || it.id === String(pid));
              if (!cartItem) return null;

              return {
                id: cartItem.internal_package_id || cartItem.id,
                name: cartItem.name,
                price: cartItem.price,
                currency: walletCurrency,
                country_id: cartItem.country_id,
                region_id: cartItem.region_id,
                gigabytes: cartItem.gigabytes,
                duration: cartItem.duration,
                top_up_available: cartItem.top_up_available,
              };
            }
          }),
        );

        const next = results.filter(Boolean);

        if (!cancelled) setPackages(next);
      } catch (e) {
        if (!cancelled) setPackages([]);
      }
    };

    loadPackages();

    return () => {
      cancelled = true;
    };
  }, [packageIds, walletCurrency, i18n.resolvedLanguage]);

  const totals = useMemo(() => {
    const subtotal = (displayItems || []).reduce((acc, cartItem) => {
      const pid = cartItem.internal_package_id || cartItem.id;
      const pkg = packages.find((p) => String(p.id) === pid);
      if (!pkg) return acc;
      const qty = cartItem.quantity || 1;
      return acc + (pkg.price || 0) * qty;
    }, 0);

    const taxes = subtotal * 0.0;
    return { subtotal, taxes, total: subtotal + taxes };
  }, [displayItems, packages]);

  if (authLoading) return null;

  return (
    <section className="checkout">
      {isLoggedIn ? (
        <div className="checkout__center-column">
          <OrderSummary
            packages={packages}
            currency={currency}
            totals={totals}
            items={displayItems}
            onPlaceOrder={handlePlaceOrder}
            promocodeData={promocodeData}
            onApplyPromocode={handleApplyPromocode}
            hidePromocode={isTopUpOrder}
            newsletterOptIn={newsletterOptIn}
            onNewsletterOptInChange={setNewsletterOptIn}
            showNewsletterCheckbox={showNewsletterCheckbox}
            speedConfirmed={speedConfirmed}
            onSpeedConfirmChange={setSpeedConfirmed}
          />
        </div>
      ) : (
        <>
          <div className="checkout__left-column">
            <AuthPanel
              email={email}
              errorMessage={errorMessage}
              onEmailChange={(e) => setEmail(e.target.value)}
              onErrorClose={() => setErrorMessage("")}
              onLoginClick={() => navigate(`/${currentLang}/sign-in`)}
              turnstileSiteKey={process.env.REACT_APP_TURNSTILE_TEST_SITE_KEY_SUCCESS}
              showTurnstile={shouldShowTurnstile}
              onTurnstileVerify={handleTurnstileVerify}
              onTurnstileExpire={handleTurnstileExpire}
              onTurnstileError={handleTurnstileError}
            />
          </div>
          <div className="checkout__right-column">
            <OrderSummary
              packages={packages}
              currency={currency}
              totals={totals}
              items={displayItems}
              orderLoading={orderLoading}
              onPlaceOrder={handlePlaceOrder}
              newsletterOptIn={newsletterOptInGuest}
              onNewsletterOptInChange={setNewsletterOptInGuest}
              showNewsletterCheckbox={true}
              speedConfirmed={speedConfirmed}
              onSpeedConfirmChange={setSpeedConfirmed}
              isGuest={true}
            />
          </div>
        </>
      )}

      <PaymentModal
        isOpen={showPaymentModal}
        type={status}
        onClose={() => setShowPaymentModal(false)}
        onConfirm={handlePaymentConfirm}
        wallet={wallet}
        loading={orderLoading}
        onlyWallet={isTopUpOrder}
        geoCountryCode={geoCountryCode}
        geoReady={geoReady}
        currency={activeCurrency}
        orderTotal={totals.total}
      />

      <StatusModal
        isOpen={showStatusModal}
        type={finalStatus}
        isLoggedIn={isLoggedIn}
        isPayPal={isPayPalPayment}
        customMessage={errorMessage}
        onClose={() => {
          if (finalStatus === "success") {
            clear();
          }
          setShowStatusModal(false);
          setFinalStatus(null);
          setErrorMessage("");
        }}
      />
    </section>
  );
};

export default CheckoutLayout;
